import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft,
    User,
    Wind,
    Flame,
    Waves,
    Leaf,
    NotebookPen,
    Calendar,
    Pill,
    Copy,
    Check,
    HeartPulse,
    Activity,
    ClipboardList,
    Plus,
    FileText,
    X,
} from "lucide-react";
import { api } from "@/lib/api";
import { describeCombo } from "@/constants/dosha";
import { TID } from "@/constants/testIds";
import { VitalsChart } from "@/pages/patient/Vitals";

const ELEMENT_ICON = { vata: Wind, pitta: Flame, kapha: Waves };

function Card({ title, kicker, icon: Icon, children, testId }) {
    return (
        <section
            data-testid={testId}
            className="border border-veda-border bg-veda-surface p-5 md:p-6"
        >
            <div className="flex items-center gap-2">
                <span className="grid h-8 w-8 place-items-center bg-veda-brand/10 text-veda-brand">
                    <Icon size={14} />
                </span>
                <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                        {kicker}
                    </p>
                    <p className="font-display mt-0.5 text-lg text-veda-onSurface md:text-xl">
                        {title}
                    </p>
                </div>
            </div>
            <div className="mt-4">{children}</div>
        </section>
    );
}

export default function DoctorPatientView() {
    const { userId } = useParams();
    const [data, setData] = useState(null);
    const [vitals, setVitals] = useState([]);
    const [tests, setTests] = useState([]);
    const [presets, setPresets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [copied, setCopied] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [main, v, t, p] = await Promise.all([
                api.get(`/doctor/patient/${userId}`),
                api.get(`/doctor/patient/${userId}/vitals`, { params: { days: 365 } }).catch(() => ({ data: { entries: [] } })),
                api.get(`/doctor/patient/${userId}/test-suggestions`).catch(() => ({ data: [] })),
                api.get(`/test-suggestions/presets`).catch(() => ({ data: { presets: [] } })),
            ]);
            setData(main.data);
            setVitals(v.data?.entries || []);
            setTests(t.data || []);
            setPresets(p.data?.presets || []);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            setErr(typeof detail === "string" ? detail : "Could not load patient.");
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        load();
    }, [load]);

    if (loading) {
        return (
            <div className="veda-container py-20 text-center text-sm text-veda-muted">
                Loading patient…
            </div>
        );
    }
    if (err) {
        return (
            <div className="veda-container py-20 text-center">
                <p className="text-sm text-veda-error">{err}</p>
                <Link
                    to="/doctor/appointments"
                    className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-veda-brand"
                >
                    <ArrowLeft size={13} /> Back to appointments
                </Link>
            </div>
        );
    }

    const { patient, profile, dosha_profile: dp, consultations, journal, health_profile: health } = data;
    const info = dp ? describeCombo(dp.primary) : null;
    const primary = (dp?.primary || "").split("-")[0];
    const DoshaIcon = ELEMENT_ICON[primary] || Leaf;

    function copyCode() {
        if (!profile?.patient_code) return;
        navigator.clipboard.writeText(profile.patient_code);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    }

    return (
        <div data-testid={TID.doctorPatientView} className="pb-16">
            <div className="border-b border-veda-border">
                <div className="veda-container py-6 md:py-10">
                    <Link
                        to="/doctor/appointments"
                        className="inline-flex items-center gap-1 text-xs font-medium text-veda-muted hover:text-veda-onSurface"
                    >
                        <ArrowLeft size={13} /> Appointments
                    </Link>
                    <div className="mt-4 flex flex-wrap items-baseline justify-between gap-3">
                        <div>
                            <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-veda-brand">
                                Patient view · read only
                            </p>
                            <h1 className="font-display mt-1 text-3xl text-veda-onSurface md:text-5xl">
                                {patient.name}
                            </h1>
                            <p className="mt-1 text-sm text-veda-muted">
                                {patient.email}
                            </p>
                        </div>
                        {profile?.patient_code && (
                            <div className="border border-veda-brand/40 bg-veda-brand/[0.06] p-4">
                                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-brand">
                                    Patient ID
                                </p>
                                <p
                                    data-testid={TID.doctorPatientCode}
                                    className="font-display mt-1 text-xl tabular-nums text-veda-onSurface"
                                >
                                    {profile.patient_code}
                                </p>
                                <button
                                    onClick={copyCode}
                                    className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-brand"
                                >
                                    {copied ? <Check size={11} /> : <Copy size={11} />}
                                    {copied ? "Copied" : "Copy"}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="veda-container grid gap-6 py-8 md:grid-cols-2">
                {/* Health profile */}
                <Card icon={User} kicker="Health profile" title="At a glance">
                    {!profile ? (
                        <p className="text-sm text-veda-muted">
                            This patient hasn't completed the onboarding form
                            yet.
                        </p>
                    ) : (
                        <>
                            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                                <dt className="text-veda-muted">Phone</dt>
                                <dd className="text-veda-onSurface">
                                    {profile.phone_country} · {profile.phone}
                                </dd>
                                <dt className="text-veda-muted">DOB</dt>
                                <dd className="text-veda-onSurface">{profile.dob}</dd>
                                <dt className="text-veda-muted">Gender</dt>
                                <dd className="capitalize text-veda-onSurface">
                                    {(profile.gender || "").replace(/_/g, " ")}
                                </dd>
                                <dt className="text-veda-muted">Height / Weight</dt>
                                <dd className="text-veda-onSurface">
                                    {profile.height_cm} cm · {profile.weight_kg} kg
                                </dd>
                                <dt className="text-veda-muted">Blood group</dt>
                                <dd className="text-veda-onSurface">
                                    {profile.blood_group}
                                </dd>
                                <dt className="text-veda-muted">City</dt>
                                <dd className="text-veda-onSurface">
                                    {profile.location}
                                </dd>
                                <dt className="text-veda-muted">Occupation</dt>
                                <dd className="text-veda-onSurface">
                                    {profile.occupation}
                                </dd>
                            </dl>
                            {profile.illnesses?.length > 0 && (
                                <div className="mt-4 border-t border-veda-divider pt-3">
                                    <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                                        Health background
                                    </p>
                                    <div className="mt-2 flex flex-wrap gap-1.5">
                                        {profile.illnesses.map((i) => (
                                            <span
                                                key={i}
                                                className="rounded-pill border border-veda-border bg-veda-surface2 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-veda-onSurface"
                                            >
                                                {i}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </Card>

                {/* Dosha */}
                <Card
                    icon={DoshaIcon}
                    kicker="Prakriti"
                    title={dp ? info?.name : "Not taken yet"}
                >
                    {dp ? (
                        <>
                            <p className="text-sm leading-relaxed text-veda-muted">
                                {info?.summary?.split(". ")[0]}.
                            </p>
                            <div className="mt-4 grid grid-cols-3 gap-3">
                                {["vata", "pitta", "kapha"].map((d) => (
                                    <div key={d} className="text-center">
                                        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-muted">
                                            {d}
                                        </p>
                                        <p className="font-display text-lg text-veda-onSurface">
                                            {dp.scores?.[d] ?? 0}%
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : (
                        <p className="text-sm text-veda-muted">
                            Patient hasn't completed the prakriti quiz yet.
                        </p>
                    )}
                </Card>

                {/* Ayurvedic health profile (self-reported by patient) */}
                <Card
                    icon={HeartPulse}
                    kicker="Ayurvedic health profile"
                    title={health ? "Self-reported by patient" : "Not filled yet"}
                >
                    {!health ? (
                        <p className="text-sm text-veda-muted">
                            The patient hasn't filled the Ayurvedic health
                            profile yet. Encourage them to add sleep, digestion,
                            stress, medicines, allergies, and complaints for
                            safer prescribing.
                        </p>
                    ) : (
                        <div className="space-y-4">
                            {(health.primary_complaint || health.current_imbalance) && (
                                <div>
                                    <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                                        Chief concern
                                    </p>
                                    {health.primary_complaint && (
                                        <p className="mt-1 text-sm text-veda-onSurface">
                                            {health.primary_complaint}
                                        </p>
                                    )}
                                    {health.current_imbalance && (
                                        <p className="mt-1 text-xs italic text-veda-muted">
                                            Imbalance: {health.current_imbalance}
                                        </p>
                                    )}
                                    {(health.secondary_complaints || []).length > 0 && (
                                        <div className="mt-2 flex flex-wrap gap-1.5">
                                            {health.secondary_complaints.map((s) => (
                                                <span
                                                    key={`sc-${s}`}
                                                    className="rounded-pill border border-veda-border bg-veda-surface2 px-2.5 py-0.5 text-[10px] font-semibold text-veda-onSurface"
                                                >
                                                    {s}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 border-t border-veda-divider pt-3 text-sm capitalize">
                                {[
                                    ["Sleep", health.sleep],
                                    ["Stress", health.stress],
                                    ["Digestion (agni)", health.digestion],
                                    ["Appetite", health.appetite],
                                    ["Energy", health.energy],
                                    ["Exercise", health.exercise],
                                    ["Bowel", health.bowel],
                                    ["Urination", health.urination],
                                    ["Food", health.food_preference],
                                ]
                                    .filter(([, v]) => v)
                                    .map(([label, v]) => (
                                        <div key={label} className="contents">
                                            <dt className="text-veda-muted normal-case">
                                                {label}
                                            </dt>
                                            <dd className="text-veda-onSurface">
                                                {String(v).replace(/_/g, " ")}
                                            </dd>
                                        </div>
                                    ))}
                            </dl>

                            {[
                                ["Allergies", health.allergies],
                                ["Medical history", health.medical_history],
                                ["Current medicines", health.current_medicines],
                                ["Previous treatments", health.previous_treatments],
                            ]
                                .filter(([, v]) => (v || []).length > 0)
                                .map(([label, items]) => (
                                    <div
                                        key={label}
                                        className="border-t border-veda-divider pt-3"
                                    >
                                        <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                                            {label}
                                        </p>
                                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                                            {items.map((it) => (
                                                <span
                                                    key={`${label}-${it}`}
                                                    className="rounded-pill border border-veda-border bg-veda-surface2 px-2.5 py-0.5 text-[11px] text-veda-onSurface"
                                                >
                                                    {it}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                ))}

                            {(health.pregnancy || health.smoking || health.alcohol) && (
                                <dl className="grid grid-cols-3 gap-3 border-t border-veda-divider pt-3 text-xs capitalize">
                                    {["pregnancy", "smoking", "alcohol"]
                                        .filter((k) => health[k])
                                        .map((k) => (
                                            <div key={k}>
                                                <dt className="text-[10px] font-semibold uppercase tracking-[0.12em] text-veda-muted">
                                                    {k}
                                                </dt>
                                                <dd className="mt-0.5 text-veda-onSurface">
                                                    {String(health[k]).replace(/_/g, " ")}
                                                </dd>
                                            </div>
                                        ))}
                                </dl>
                            )}

                            {health.notes && (
                                <div className="border-t border-veda-divider pt-3">
                                    <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                                        Notes from patient
                                    </p>
                                    <p className="mt-1 text-sm italic leading-relaxed text-veda-onSurface">
                                        {health.notes}
                                    </p>
                                </div>
                            )}

                            <p className="text-[10px] text-veda-muted">
                                Updated {new Date(health.updated_at).toLocaleString()}
                            </p>
                        </div>
                    )}
                </Card>

                {/* Journal */}
                <Card
                    icon={NotebookPen}
                    kicker="Wellness journal"
                    title={
                        journal.length
                            ? `${journal.length} recent entr${journal.length === 1 ? "y" : "ies"}`
                            : "No entries yet"
                    }
                >
                    {journal.length === 0 ? (
                        <p className="text-sm text-veda-muted">
                            When the patient logs mood / sleep / energy, entries
                            appear here.
                        </p>
                    ) : (
                        <ul className="grid grid-cols-4 gap-1 sm:grid-cols-7">
                            {journal.slice(0, 14).map((j) => (
                                <li
                                    key={j.entry_id}
                                    title={`${j.date} · mood ${j.mood}/5, sleep ${j.sleep}/5, energy ${j.energy}/5${j.note ? " — " + j.note : ""}`}
                                    className="border border-veda-border bg-veda-surface2 p-2 text-center"
                                >
                                    <p className="text-[9px] font-semibold uppercase text-veda-muted">
                                        {j.date.slice(5)}
                                    </p>
                                    <p className="mt-1 text-[10px] tabular-nums text-veda-onSurface">
                                        {j.mood}·{j.sleep}·{j.energy}
                                    </p>
                                </li>
                            ))}
                        </ul>
                    )}
                    <p className="mt-2 text-[9px] text-veda-muted">
                        mood · sleep · energy (1–5)
                    </p>
                </Card>

                {/* Consultations with prescriptions */}
                <Card
                    icon={Calendar}
                    kicker="Your consultations"
                    title={
                        consultations.length
                            ? `${consultations.length} with this patient`
                            : "No consultations"
                    }
                >
                    {consultations.length === 0 ? (
                        <p className="text-sm text-veda-muted">
                            No past consultations with this patient.
                        </p>
                    ) : (
                        <ul className="divide-y divide-veda-divider">
                            {consultations.map((c) => (
                                <li
                                    key={c.consultation_id}
                                    className="py-3"
                                >
                                    <p className="text-xs font-semibold text-veda-onSurface">
                                        {c.slot} ·{" "}
                                        <span className="capitalize">
                                            {c.mode}
                                        </span>{" "}
                                        ·{" "}
                                        <span className="capitalize text-veda-muted">
                                            {c.status || "confirmed"}
                                        </span>
                                    </p>
                                    {c.notes && (
                                        <p className="mt-1 text-xs italic text-veda-muted">
                                            Note: {c.notes}
                                        </p>
                                    )}
                                    {(c.prescriptions || []).length > 0 && (
                                        <ul className="mt-2 flex flex-col gap-1.5 border-l-2 border-veda-brand pl-3">
                                            {c.prescriptions.map((r) => (
                                                <li
                                                    key={r.prescription_id}
                                                    className="flex items-start gap-2 text-[11px] text-veda-onSurface"
                                                >
                                                    <Pill
                                                        size={11}
                                                        className="mt-0.5 shrink-0 text-veda-brand"
                                                    />
                                                    <span>
                                                        <span className="font-semibold">
                                                            {r.medicine}
                                                        </span>
                                                        {" · "}
                                                        {r.dosage}
                                                        {" · "}
                                                        {r.frequency}
                                                        {" · "}
                                                        {r.days}d
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    <Link
                                        to={`/consultation/${c.consultation_id}`}
                                        className="mt-2 inline-block text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-brand"
                                    >
                                        Open chat →
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </Card>

                {/* Vitals timeline */}
                <Card
                    icon={Activity}
                    kicker="Vitals timeline"
                    title={
                        vitals.length
                            ? `${vitals.length} reading${vitals.length === 1 ? "" : "s"}`
                            : "No vitals logged"
                    }
                    testId={TID.doctorPatientVitals}
                >
                    {vitals.length === 0 ? (
                        <p className="text-sm text-veda-muted">
                            Encourage the patient to log BP, weight, sugar, and
                            pulse so trends surface here for future consults.
                        </p>
                    ) : (
                        <VitalsGroup entries={vitals} />
                    )}
                </Card>

                {/* Lab tests suggested & reports */}
                <div className="md:col-span-2">
                    <DoctorTests
                        patientId={userId}
                        tests={tests}
                        presets={presets}
                        onChanged={load}
                    />
                </div>
            </div>
        </div>
    );
}

function VitalsGroup({ entries }) {
    const grouped = new Map();
    for (const e of entries) {
        const key = e.metric === "custom" ? `custom::${e.custom_name || "Custom"}` : e.metric;
        if (!grouped.has(key)) grouped.set(key, []);
        grouped.get(key).push(e);
    }
    return (
        <div className="grid gap-4 sm:grid-cols-2">
            {[...grouped.entries()].map(([k, items]) => {
                const first = items[0];
                const label =
                    first.metric === "custom" ? first.custom_name || "Custom" : first.metric.replace(/_/g, " ");
                const latest = items[items.length - 1];
                const display =
                    first.metric === "bp"
                        ? `${Math.round(latest.value)}/${Math.round(latest.secondary)} ${latest.unit || ""}`
                        : `${latest.value}${latest.unit ? " " + latest.unit : ""}`;
                return (
                    <div key={k} className="border border-veda-border bg-veda-surface2 p-3">
                        <div className="flex items-baseline justify-between gap-2">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-veda-muted">
                                {label}
                            </p>
                            <p className="font-display text-sm text-veda-onSurface tabular-nums">
                                {display}
                            </p>
                        </div>
                        <div className="mt-1">
                            <VitalsChart
                                metric={first.metric}
                                unit={first.unit}
                                custom_name={first.custom_name}
                                entries={items}
                            />
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function DoctorTests({ patientId, tests, presets, onChanged }) {
    const [name, setName] = useState("");
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);

    async function suggest(e) {
        e.preventDefault();
        const n = name.trim();
        if (!n) {
            toast.error("Pick a test name.");
            return;
        }
        setSaving(true);
        try {
            await api.post(`/doctor/test-suggestions`, {
                patient_id: patientId,
                test_name: n,
                notes: notes.trim() || null,
            });
            setName("");
            setNotes("");
            toast.success("Test suggested.");
            onChanged?.();
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Could not suggest.");
        } finally {
            setSaving(false);
        }
    }

    async function patch(sid, body) {
        try {
            await api.patch(`/test-suggestions/${sid}`, body);
            onChanged?.();
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Could not update.");
        }
    }

    async function openReport(sid, rid) {
        try {
            const { data } = await api.get(`/test-suggestions/${sid}/report/${rid}`);
            const w = window.open();
            if (w && data?.data_url) {
                if ((data.mime_type || "").startsWith("image/")) {
                    w.document.write(`<img src="${data.data_url}" style="max-width:100%;height:auto"/>`);
                } else if (data.mime_type === "application/pdf") {
                    w.document.write(`<iframe src="${data.data_url}" style="border:0;width:100vw;height:100vh"></iframe>`);
                } else {
                    w.document.write(`<a href="${data.data_url}" download="${data.filename}">Download ${data.filename}</a>`);
                }
            }
        } catch {
            toast.error("Could not open report.");
        }
    }

    return (
        <section
            data-testid={TID.doctorPatientTests}
            className="border border-veda-border bg-veda-surface p-5 md:p-6"
        >
            <div className="flex items-center gap-2">
                <span className="grid h-8 w-8 place-items-center bg-veda-brand/10 text-veda-brand">
                    <ClipboardList size={14} />
                </span>
                <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                        Lab tests
                    </p>
                    <p className="font-display text-lg text-veda-onSurface">
                        Suggested tests & uploaded reports
                    </p>
                </div>
            </div>

            {/* Suggest a new test */}
            <form
                onSubmit={suggest}
                data-testid={TID.doctorSuggestTestForm}
                className="mt-4 grid gap-3 border border-veda-border bg-veda-surface2/50 p-4"
            >
                <div className="flex flex-wrap items-start gap-2">
                    <label className="min-w-[200px] flex-1">
                        <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                            Test
                        </span>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="CBC, Vitamin D…"
                            data-testid={TID.doctorSuggestTestName}
                            className="mt-1.5 w-full border border-veda-border bg-veda-surface px-3 py-2 text-sm focus:border-veda-brand focus:outline-none"
                        />
                    </label>
                    <label className="min-w-[220px] flex-1">
                        <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                            Clinical note (optional)
                        </span>
                        <input
                            type="text"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Rule out anemia…"
                            data-testid={TID.doctorSuggestTestNotes}
                            className="mt-1.5 w-full border border-veda-border bg-veda-surface px-3 py-2 text-sm focus:border-veda-brand focus:outline-none"
                        />
                    </label>
                    <button
                        type="submit"
                        disabled={saving}
                        data-testid={TID.doctorSuggestTestSubmit}
                        className="mt-6 inline-flex items-center gap-1 border border-veda-brand bg-veda-brand px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-white disabled:opacity-60"
                    >
                        <Plus size={12} /> {saving ? "Adding…" : "Suggest"}
                    </button>
                </div>
                {presets.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                        {presets.map((p) => (
                            <button
                                type="button"
                                key={p}
                                onClick={() => setName(p)}
                                className="rounded-pill border border-veda-border bg-veda-surface px-2.5 py-0.5 text-[11px] font-semibold text-veda-onSurface hover:border-veda-brand hover:text-veda-brand"
                            >
                                {p}
                            </button>
                        ))}
                    </div>
                )}
            </form>

            {/* List */}
            {tests.length === 0 ? (
                <p className="mt-4 text-sm text-veda-muted">
                    Nothing suggested yet.
                </p>
            ) : (
                <ul className="mt-4 space-y-2">
                    {tests.map((t) => (
                        <li
                            key={t.suggestion_id}
                            className="border border-veda-border bg-veda-surface2/40 p-3"
                        >
                            <div className="flex flex-wrap items-baseline justify-between gap-2">
                                <div>
                                    <p className="font-display text-base text-veda-onSurface">
                                        {t.test_name}{" "}
                                        <span className="text-[10px] uppercase tracking-[0.14em] text-veda-muted">
                                            · {t.status}
                                            {t.requested_by === "patient" && " (by patient)"}
                                        </span>
                                    </p>
                                    {t.notes && (
                                        <p className="mt-0.5 text-xs italic text-veda-muted">
                                            {t.notes}
                                        </p>
                                    )}
                                </div>
                                <div className="flex flex-wrap items-center gap-1.5">
                                    {t.status === "requested" && (
                                        <button
                                            type="button"
                                            onClick={() => patch(t.suggestion_id, { status: "suggested" })}
                                            data-testid={TID.doctorAcceptRequest(t.suggestion_id)}
                                            className="inline-flex items-center gap-1 border border-veda-brand bg-veda-brand px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-white"
                                        >
                                            <Check size={11} /> Accept
                                        </button>
                                    )}
                                    {t.status !== "done" && (
                                        <button
                                            type="button"
                                            onClick={() => patch(t.suggestion_id, { status: "done" })}
                                            className="inline-flex items-center gap-1 border border-veda-border bg-veda-surface px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-onSurface hover:border-veda-brand hover:text-veda-brand"
                                        >
                                            <Check size={11} /> Done
                                        </button>
                                    )}
                                    {t.status !== "declined" && (
                                        <button
                                            type="button"
                                            onClick={() => patch(t.suggestion_id, { status: "declined" })}
                                            className="inline-flex items-center gap-1 border border-veda-border bg-veda-surface px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-muted hover:border-veda-error hover:text-veda-error"
                                        >
                                            <X size={11} /> Decline
                                        </button>
                                    )}
                                </div>
                            </div>
                            {(t.reports || []).length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                    {t.reports.map((r) => (
                                        <button
                                            type="button"
                                            key={r.report_id}
                                            onClick={() => openReport(t.suggestion_id, r.report_id)}
                                            className="inline-flex items-center gap-1 rounded-pill border border-veda-brand/40 bg-veda-brand/[0.06] px-2.5 py-1 text-[11px] font-medium text-veda-brand hover:border-veda-brand"
                                        >
                                            <FileText size={11} />
                                            <span className="max-w-[160px] truncate">
                                                {r.filename}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}
